import { LabAcquisitionStatus } from '@/features/labs/LabAcquisitionStatus';
import { useLabsExperiment } from "@/features/labs/labExperiment";
import { format, parseISO } from "date-fns";
import { Link } from "react-router-dom";

import { useEffect, useRef, type ReactNode } from "react";

import {
  useBootstrap,
  useClinicalState,
  useGuidedSelfInjection,
  useHome,
  useMarketplace,
  useMyReferral,
  useRequiredIntakes,
  useRenewalCheckins,
  useTreatmentOffers,
} from "@/lib/api/hooks";
import { Icon } from "@/lib/Icon";
import { PageHeader } from "@/components/app-shell";
import { track } from "@/lib/analytics";
import { useExperimentVariant } from "@/lib/experiments";
import {
  PERSONALIZED_RECOMMENDATIONS_EXPERIMENT_KEY,
  PERSONALIZED_RECOMMENDATIONS_VARIANT,
} from "@/lib/personalizedRecommendations";
import type { TreatmentOffer } from "@/types/api";
import { canResumeTreatmentOffer } from "@/features/treatment-offers/treatmentOfferPurchase";
import type { HomeTask } from "@/types/bootstrap";
import { buildHomeTasks } from "./homeTasks";
import { Button } from "@/design-system/primitives";
import { TodaysPlan } from "./components/TodaysPlan";
import { TaskList } from "./components/TaskList";
import { ResourceHub } from "./components/ResourceHub";
import { MembershipHomeSlot } from '@/features/membership/MembershipHomeSlot';
import { MemberCodeCard } from "./components/MemberCodeCard";
import { MembershipHomeCard } from "./components/MembershipHomeCard";
import { PersonalizedProductsCard } from "./components/PersonalizedProductsCard";
import { InFlightPurchasesCard } from "./components/InFlightPurchasesCard";
import { GuidedSelfInjectionCard } from "@/features/guided-injection/components/GuidedSelfInjectionCard";

export function HomeScreen() {
  const { enabled: labsEnabled } = useLabsExperiment();
  useBootstrap(); // warm cache for downstream guards / shell
  const { data: home, isLoading } = useHome();
  const { data: clinical } = useClinicalState();
  const { data: guidedSelfInjection } = useGuidedSelfInjection();
  const { data: requiredIntakes, isFetched: requiredIntakesFetched } =
    useRequiredIntakes();
  const marketplace = useMarketplace();
  const renewalCheckins = useRenewalCheckins();
  const treatmentOffers = useTreatmentOffers();
  const recommendationsVariant = useExperimentVariant(
    PERSONALIZED_RECOMMENDATIONS_EXPERIMENT_KEY,
  );
  // Referrals rolled out to 100% (gift-referrals experiment completed
  // 2026-07-13, variant-a won) — every patient gets a member code.
  const { data: referral } = useMyReferral(true);
  const pendingIntakes = (requiredIntakes ?? []).filter(
    (i) => !i.intake_submitted_at,
  );
  const recommendationCount = marketplace.data?.recommendations?.length ?? 0;
  const recommendationsExposureRef = useRef(false);
  const availableTreatmentOffers = (treatmentOffers.data?.offers ?? []).filter(
    (offer) => offer.status === "offered" || canResumeTreatmentOffer(offer),
  );
  // Scroll target for the top-of-home "Complete Tasks" jump button.
  const tasksRef = useRef<HTMLElement>(null);

  // Count an exposure only when this home slot can actually render. Both
  // cohorts wait for the recommendation response so `eligible` is a balanced
  // analysis dimension rather than something only known for variant-a.
  useEffect(() => {
    if (
      recommendationsExposureRef.current ||
      recommendationsVariant == null ||
      !requiredIntakesFetched ||
      pendingIntakes.length > 0 ||
      !marketplace.isFetched
    ) {
      return;
    }
    const showsRecommendations =
      recommendationsVariant === PERSONALIZED_RECOMMENDATIONS_VARIANT &&
      recommendationCount > 0;
    if (!showsRecommendations && (labsEnabled || !referral)) return;

    recommendationsExposureRef.current = true;
    track("$experiment_viewed", {
      experiment_key: PERSONALIZED_RECOMMENDATIONS_EXPERIMENT_KEY,
      variant_key: recommendationsVariant,
      source: "home_recommendations_card",
      eligible: recommendationCount > 0,
      recommendation_count: recommendationCount,
      recommendation_model: marketplace.data?.recommendation_model ?? null,
      rendered_experience: showsRecommendations
        ? "personalized_recommendations"
        : recommendationCount > 0
          ? "referral_control"
          : "referral_ineligible_fallback",
    });
  }, [
    marketplace.data?.recommendation_model,
    marketplace.isFetched,
    pendingIntakes.length,
    recommendationCount,
    recommendationsVariant,
    referral,
    requiredIntakesFetched,
    labsEnabled,
  ]);
  if (isLoading || !home) return <HomeSkeleton />;

  // Backend filters appointments to status=scheduled in the home payload.
  // Sort soonest-first so the next visit is on top.
  const upcoming = [...(home.appointments ?? [])]
    // Guided injection visits own their lifecycle + actions in the dedicated
    // entitlement card below. Keeping them out of the generic list prevents a
    // patient from entering the Dr. Hinds sync-visit booking surface.
    .filter((appointment) => appointment.kind !== "guided_self_injection")
    .sort((a, b) => +parseISO(a.start_at) - +parseISO(b.start_at));
  // Lander-purchase orders mid-fulfillment. Show the SKU + status
  // pill front-and-center so the patient sees their purchase progressing
  // (awaiting Rx → processing → shipped → delivered) without hunting
  // through the Orders tab.
  const pendingIntakeProductIds = new Set(
    pendingIntakes.map((i) => i.product_id),
  );
  const inFlightPurchases = (clinical?.prescribed_medications ?? []).filter(
    (p) =>
      p.purchase_state === "paid" &&
      Boolean(p.pharmacy_order_status) &&
      !["shipped", "delivered", "cancelled", "failed", "superseded"].includes(
        p.pharmacy_order_status ?? "",
      ) &&
      // Intake-pending takes priority — surface there, not here.
      !(p.product_id && pendingIntakeProductIds.has(p.product_id)),
  );

  // Pending intakes render as first-class task cards in the Tasks section
  // (leading the list; clinician is waiting). Shared with the landing
  // IntakeAttentionDrawer via buildHomeTasks so the two never diverge.
  const tasks: HomeTask[] = buildHomeTasks(
    home.tasks ?? [], pendingIntakes, renewalCheckins.data?.items,
  );

  const firstName =
    (home.greeting.first_name ?? "").trim().toUpperCase() || "THERE";

  const personalizedItems = (marketplace.data?.recommendations ?? [])
    .map((recommendation) => {
      const product = marketplace.data?.products.find(
        (candidate) => candidate.id === recommendation.product_id,
      );
      return product ? { product, recommendation } : null;
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);
  const showPersonalizedRecommendations =
    recommendationsVariant === PERSONALIZED_RECOMMENDATIONS_VARIANT &&
    personalizedItems.length > 0;
  // No greeting hijack for pending intakes — they render as ordinary task
  // cards below, so the header stays the standard welcome and the intro copy
  // counts them like any other task.
  const headerTitle = (
    <>
      Hi {firstName},
      <br />
      Welcome back.
    </>
  );
  // Recommendations only count toward the greeting when they actually render
  // (variant-a, matches, and the recs band isn't suppressed by a pending
  // intake) so the copy never promises a card that isn't shown.
  const recsShownCount =
    requiredIntakesFetched &&
    pendingIntakes.length === 0 &&
    showPersonalizedRecommendations
      ? personalizedItems.length
      : 0;
  const headerDescription =
    homeIntro(tasks.length, recsShownCount) ?? statusBlurb(home);

  // "Complete Tasks" jump button — lives in the header band below the
  // description (design), scrolls to the task list. Carries the guided
  // tour's homepage spotlight anchor (previously the date strip). Only
  // shown when there are tasks to complete.
  const completeTasksButton =
    tasks.length > 0 ? (
      <button
        type="button"
        data-tour="home-status"
        onClick={() =>
          tasksRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          })
        }
        className="w-full flex items-center justify-between gap-10 h-[4.4rem] px-15 bg-[#707889] text-white hover:bg-[#8D929E] font-body text-15 text-ink"
      >
        <span className="blink">
          {tasks.length === 1 ? "Complete Task" : "Complete Tasks"}
        </span>
        <span
          aria-hidden
          className="blink inline-flex items-center justify-center"
        >
          <Icon
            name="Arrow"
            viewBox="0 0 10 12"
            className="w-[.8rem] rotate-90"
          />
        </span>
      </button>
    ) : null;

  return (
    <div className="flex flex-col">
      <PageHeader
        title={headerTitle}
        description={headerDescription}
        action={completeTasksButton}
      />

      <div className="flex flex-col w-full pb-25">
        <div className="px-20 md:px-30 empty:hidden"><LabAcquisitionStatus includePaid={!labsEnabled} /></div>
        {/* Personalized recommendations — full-width sunken band, flush under
            the (also sunken) header. Full-width sections own their padding +
            background and live OUTSIDE the padded column below. Hidden while
            the patient has intakes to finish so it doesn't distract. */}
        {requiredIntakesFetched &&
          pendingIntakes.length === 0 &&
          showPersonalizedRecommendations && (
            <PersonalizedProductsCard
              items={personalizedItems}
              recommendationModel={
                marketplace.data?.recommendation_model ?? null
              }
            />
          )}

        {/* Padded, centered content column — everything here shares the same
            gutter. Full-width/background sections go above this, not inside. */}
        <div className="flex flex-col gap-30 max-w-[65rem] mx-auto w-full px-15 md:px-40 pt-25">
          {availableTreatmentOffers.length > 0 && (
            <TreatmentOfferCard offers={availableTreatmentOffers} />
          )}

          {/* Today's plan — one section per slot, eyebrow + medication cards. */}
          <TodaysPlan slots={home.today} />

          {/* System Membership perks; renders nothing for non-members. */}
          <MembershipHomeCard />

          {/* Variant A replaces the persistent referral card with Labs; the
              membership A/B offers System Membership in that slot instead. */}
          {labsEnabled ? <MembershipHomeSlot /> : referral && <MemberCodeCard referral={referral} surface="home" />}

          {/* data-tour: the guided tour's homepage step reveals the
              order-status card alongside the task bar. */}
          {inFlightPurchases.length > 0 && (
            <div data-tour="home-order-status">
              <InFlightPurchasesCard
                prescriptions={inFlightPurchases}
                needsSyncConsult={Boolean(clinical?.needs_sync_consult)}
                awaitingConsult={Boolean(clinical?.awaiting_consult)}
              />
            </div>
          )}

          {guidedSelfInjection?.status &&
            (guidedSelfInjection.entitled ||
              guidedSelfInjection.status === "refunded") &&
            guidedSelfInjection.experiment &&
            guidedSelfInjection.presentation &&
            guidedSelfInjection.preparation && (
              <GuidedSelfInjectionCard
                state={{
                  ...guidedSelfInjection,
                  status: guidedSelfInjection.status,
                  experiment: guidedSelfInjection.experiment,
                  presentation: guidedSelfInjection.presentation,
                  preparation: guidedSelfInjection.preparation,
                }}
              />
            )}

          {upcoming.length > 0 && (
            <section className="flex flex-col gap-10">
              <div className="flex items-center justify-between gap-10">
                <h2 className="font-mono text-12 uppercase text-[#7C7C7C]">
                  Upcoming
                </h2>
                <Link to="/app/clinic?tab=appointments" className="footer-link">
                  View all
                </Link>
              </div>
              <ul className="flex flex-col gap-10">
                {upcoming.slice(0, 3).map((a) => {
                  const start = parseISO(a.start_at);
                  return (
                    <li key={a.id}>
                      <Link
                        to="/app/clinic?tab=appointments"
                        className="block hover:opacity-90 bg-raised"
                      >
                        <div className="flex items-stretch border border-[#ECECEC]">
                          <div className="w-[6rem] shrink-0 bg-[#ECECEC] flex items-center justify-center text-[#7C7C7C]">
                            <div className="w-25">
                              <Icon name="Mark Line" viewBox="0 0 32 30" />
                            </div>
                          </div>
                          <div className="flex-1 min-w-0 px-15 py-15 flex flex-col gap-5">
                            <div className="font-mono text-15 uppercase text-black leading-110">
                              {format(start, "EEE, MMM d · h:mm a")}
                            </div>
                            <div className="font-body text-13 text-[#7C7C7C]">
                              {a.clinician_name ?? "Provider TBD"} ·{" "}
                              {a.kind.replace(/_/g, " ")} · {a.modality}
                            </div>
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {/* Tasks — row-style list per design. Section header always shown
              when there are any tasks (drops the special-case single-task
              standalone treatment). */}
          {(renewalCheckins.isError || renewalCheckins.data?.unavailable) && (
            <div role="status" className="border border-line-subtle bg-raised p-15 text-16 text-ink-secondary">
              <p>We couldn't check all upcoming refill forms. Any other pending tasks remain available below.</p>
              <Button variant="secondary" className="mt-10 min-h-[44px]" onClick={() => { void renewalCheckins.refetch(); }}>
                Retry check-ins
              </Button>
            </div>
          )}
          {tasks.length > 0 && (
            <section ref={tasksRef} className="flex flex-col scroll-mt-25">
              <div className="flex items-center justify-between gap-10 bg-[#ECECEC] p-10 font-mono uppercase text-12">
                <h2 className="font-mono text-12 uppercase text-[#7C7C7C]">
                  {tasks.length === 1 ? "Task" : "Tasks"}
                </h2>
                <span className="font-mono text-12 text-[#7C7C7C]">
                  [{String(tasks.length).padStart(2, "0")}]
                </span>
              </div>
              <TaskList tasks={tasks} />
            </section>
          )}

          {/* Resources — row-style list. */}
          {home.resource_hub.length > 0 && (
            <section className="flex flex-col gap-10">
              <h2 className="font-mono text-12 uppercase text-[#7C7C7C]">
                Resources
              </h2>
              <ResourceHub items={home.resource_hub} />
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function TreatmentOfferCard({ offers }: { offers: TreatmentOffer[] }) {
  return (
    <section className="border border-success/30 bg-success-wash">
      <div className="px-14 py-9 border-b border-success/25 flex items-center justify-between gap-10">
        <div>
          <p className="font-mono text-12 uppercase text-success">
            Approved after your visit
          </p>
          <p className="text-13 text-ink-secondary mt-3">
            Your clinician already completed the clinical review.
          </p>
        </div>
        <span className="font-mono text-12 text-success">
          [{String(offers.length).padStart(2, "0")}]
        </span>
      </div>
      <div className="divide-y divide-success/25">
        {offers.map((offer) => (
          <div
            key={offer.id}
            className="p-14 flex flex-wrap items-center justify-between gap-12"
          >
            <div className="min-w-0">
              <h2 className="font-mono text-16 uppercase leading-110">
                {offer.product_label}
              </h2>
              <p className="text-13 text-ink-secondary mt-5">
                {offer.medication_name}
              </p>
            </div>
            <Link
              to={`/app/treatment-offers/${offer.id}`}
              aria-label={`${canResumeTreatmentOffer(offer) ? "Resume purchase of" : "Review and purchase"} ${offer.product_label}`}
              className="min-h-40 px-14 inline-flex items-center justify-center bg-ink text-page type-label-sm hover:opacity-85"
            >
              {canResumeTreatmentOffer(offer)
                ? "Resume purchase"
                : "Review & purchase"}
            </Link>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Greeting-band description copy. Returns a ReactNode so inline links
 * (e.g. "Clinic" → /app/clinic) render properly inside the PageHeader
 * description slot. Owns both populated and empty states so TodaysPlan
 * stays a pure slot renderer.
 */
/**
 * Dynamic greeting-band intro summarizing open tasks + new recommendations.
 * Returns null when there's nothing to summarize so the caller can fall back
 * to the schedule blurb. Both counts are pluralized; either clause is dropped
 * when its count is zero.
 */
function homeIntro(taskCount: number, recCount: number): string | null {
  const parts: string[] = [];
  if (taskCount > 0) {
    parts.push(
      `${taskCount} important ${taskCount === 1 ? "task" : "tasks"} awaiting`,
    );
  }
  if (recCount > 0) {
    parts.push(
      `${recCount} new health ${
        recCount === 1 ? "recommendation" : "recommendations"
      } based on our clinician’s review of your goals`,
    );
  }
  if (parts.length === 0) return null;
  return `You have ${parts.join(", and ")}.`;
}

function statusBlurb(home: { today: { medications: unknown[] }[] }): ReactNode {
  const totalToday = home.today.reduce(
    (sum, slot) => sum + (slot.medications?.length ?? 0),
    0,
  );
  if (totalToday === 0) {
    return (
      <>
        Nothing scheduled for today. If that&apos;s wrong, message your
        clinician via{" "}
        <Link to="/app/clinic" className="underline hover:text-black">
          Clinic
        </Link>
        .
      </>
    );
  }
  return `${totalToday} ${totalToday === 1 ? "administration" : "administrations"} scheduled today.`;
}

function HomeSkeleton() {
  return (
    <div className="flex flex-col gap-40 animate-pulse">
      <div className="h-[4rem] w-[32rem] max-w-full bg-sunken" />
      <div className="h-[2rem] w-[20rem] max-w-full bg-sunken" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        <div className="h-[8rem] bg-sunken" />
        <div className="h-[8rem] bg-sunken" />
      </div>
    </div>
  );
}
